import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Search, Sparkles, CornerDownLeft, Mic } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";

const PARAMS = [
  { key: "language", labelKey: "search.language", options: ["Auto", "IT", "EN"] },
  { key: "reasoning", labelKey: "search.reasoning", options: ["Fast", "Balanced", "Deep"] },
  { key: "attitude", labelKey: "search.attitude", options: ["Neutral", "Creative", "Critical"] },
];

const ParamToggle = ({ param, value, onChange, t }) => (
  <div className="flex items-center gap-3">
    <span className="font-mono text-[11px] uppercase tracking-widest text-[#A7B4C2]/70">
      {t(param.labelKey)}
    </span>
    <div className="flex rounded-lg border border-white/10 bg-[#05101f]/60 p-0.5">
      {param.options.map((opt) => {
        const activeOpt = value === opt;
        return (
          <button
            key={opt}
            data-testid={`param-${param.key}-${opt.toLowerCase()}`}
            onClick={() => onChange(param.key, opt)}
            className="relative px-3 py-1 rounded-md font-mono text-[12px] transition-colors duration-300"
          >
            {activeOpt && (
              <motion.span
                layoutId={`param-pill-${param.key}`}
                className="absolute inset-0 rounded-md bg-[#1E6FFF]"
                transition={{ type: "spring", stiffness: 400, damping: 32 }}
              />
            )}
            <span
              className={`relative z-10 ${
                activeOpt ? "text-white" : "text-[#A7B4C2] hover:text-[#4BE3FF]"
              }`}
            >
              {t(`search.opt.${opt}`)}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export const CognitiveSearch = ({ compact = false, initialQuery = "" }) => {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [query, setQuery] = useState(initialQuery);
  const [focused, setFocused] = useState(false);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const [params, setParams] = useState({
    language: "Auto",
    reasoning: "Balanced",
    attitude: "Creative",
  });

  const onChange = (key, value) => setParams((p) => ({ ...p, [key]: value }));

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort?.();
    };
  }, []);

  const toggleVoice = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error(t("search.micUnsupported"));
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = lang === "it" ? "it-IT" : "en-US";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join("");
      setQuery(transcript);
    };
    recognition.onerror = () => {
      toast.error(t("search.micError"));
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const submit = () => {
    const q =
      query ||
      "How do transformer models achieve contextual understanding?";
    let language;
    if (params.language === "IT") language = "Italian";
    else if (params.language === "EN") language = "English";
    else language = lang === "it" ? "Italian" : "English"; // Auto follows the site language
    navigate(
      `/results?q=${encodeURIComponent(q)}` +
        `&reasoning=${params.reasoning}&attitude=${params.attitude}&language=${language}`
    );
  };

  return (
    <div className={compact ? "w-full" : "w-full max-w-3xl"}>
      <motion.div
        animate={{
          boxShadow: focused
            ? "0 0 70px -6px rgba(75,227,255,0.55), inset 0 0 0 1px rgba(75,227,255,0.6)"
            : "0 0 40px -12px rgba(75,227,255,0.25), inset 0 0 0 1px rgba(255,255,255,0.08)",
        }}
        transition={{ duration: 0.4 }}
        className="glass rounded-2xl p-2 flex items-center gap-2"
      >
        <div className="pl-4 text-[#4BE3FF]">
          <Search className="w-5 h-5" />
        </div>
        <input
          data-testid="cognitive-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={t("search.placeholder")}
          className="flex-1 bg-transparent outline-none font-mono text-[15px] md:text-base text-white placeholder:text-[#A7B4C2]/60 py-3"
        />
        <button
          data-testid="voice-search-button"
          onClick={toggleVoice}
          aria-label={listening ? t("search.micStop") : t("search.micStart")}
          title={listening ? t("search.micStop") : t("search.micStart")}
          className={`relative flex items-center justify-center w-10 h-10 rounded-xl border transition-colors duration-300 ${
            listening
              ? "border-[#4BE3FF] text-[#4BE3FF]"
              : "border-white/10 text-[#A7B4C2] hover:text-[#4BE3FF] hover:border-[#4BE3FF]/50"
          }`}
        >
          {listening && (
            <motion.span
              className="absolute inset-0 rounded-xl bg-[#4BE3FF]/20"
              animate={{ opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            />
          )}
          <Mic className="w-4 h-4 relative z-10" />
        </button>
        <button
          data-testid="cognitive-search-submit"
          onClick={submit}
          className="group flex items-center gap-2 rounded-xl bg-[#1E6FFF] hover:bg-[#4BE3FF] hover:text-[#0A1A2F] text-white font-mono text-sm px-4 md:px-5 py-3 transition-colors duration-300"
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden sm:inline">{t("search.reason")}</span>
          <CornerDownLeft className="w-3.5 h-3.5 opacity-60" />
        </button>
      </motion.div>

      {!compact && (
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 pl-1">
          {PARAMS.map((p) => (
            <ParamToggle
              key={p.key}
              param={p}
              value={params[p.key]}
              onChange={onChange}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};
